import { INSIGHTS_FIELD, LinqsetNode, NODE_SUPERTYPE, getIndexValue, isRolePeople, isSystemInfrastructure, isRoleSystem, CURRENT_STATE_PROJECT_KEY, isEditableOrganisationNode, getInsightsForNode, NODE_TYPE, NodeInsights } from "../../common/linqset-model"
import { ACTION_TYPE, CARBON_SCOPE, FUNCTIONS, getTotalModelCost } from "../../common/organisation"
import { MODEL_COLOURS } from "../../model-services/model-item-enums"
import { dataTypeUsesTotals, NODE_TYPE_DATA_TYPE, NodeTypeData } from "../node-types/node-type-data"

// the work and those doing it show the value and revenue they contribute, which add up, rather than all they support,
// which counts the same value again at every step. Outputs, outcomes and models create the value, so keep their own,
// and an input's is the value of all that comes from it
export const usesContribution = (superType:NODE_SUPERTYPE, dataType:NODE_TYPE_DATA_TYPE) =>
    (dataType === NODE_TYPE_DATA_TYPE.STANDARD || dataType === NODE_TYPE_DATA_TYPE.GROUP) && (superType === NODE_SUPERTYPE.ACTION || superType === NODE_SUPERTYPE.ROLE || superType === NODE_SUPERTYPE.SYSTEM)

export const contributionField = (field:INSIGHTS_FIELD, superType:NODE_SUPERTYPE, dataType:NODE_TYPE_DATA_TYPE) =>
    !usesContribution(superType, dataType) ? field
        : field === INSIGHTS_FIELD.VALUE ? INSIGHTS_FIELD.VALUE_CONTRIBUTION
        : field === INSIGHTS_FIELD.REVENUE ? INSIGHTS_FIELD.REVENUE_CONTRIBUTION
        : field

export const getInsightsValueForNode = (type:INSIGHTS_FIELD, insights:NodeInsights, dataType:NODE_TYPE_DATA_TYPE, superType?:NODE_SUPERTYPE) => {
    const useTotals = dataTypeUsesTotals(dataType, superType)

    switch (type) {
        case INSIGHTS_FIELD.COST:
            const costsAllocations = useTotals?insights.totalAllocatedCostsByType:insights.allocatedCostsByType
            if (costsAllocations && costsAllocations.map)
                return [costsAllocations.map[NODE_SUPERTYPE.INFRASTRUCTURE]||0, costsAllocations.map[NODE_SUPERTYPE.SYSTEM]||0, costsAllocations.map[NODE_SUPERTYPE.PEOPLE]||0, costsAllocations.map[NODE_SUPERTYPE.ROLE]||0]
            else return [0,0,0,0]
        case INSIGHTS_FIELD.HOURS_ITEM:
            if (insights.numberProcessed === 0) return undefined
            return useTotals?[Math.round(100*insights.totalHours/insights.numberProcessed)/100]:[Math.round(100*insights.hours/insights.numberProcessed)/100]
        case INSIGHTS_FIELD.COST_ITEM:
            if (insights.numberProcessed === 0) return undefined
            return useTotals?[Math.round(100*(insights.totalAllocatedCostsByType.total)/insights.numberProcessed)/100]:[Math.round(100*(insights.allocatedCostsByType.total)/insights.numberProcessed)/100]    
        case INSIGHTS_FIELD.ANNUAL_HOURS:
            return useTotals?[insights.totalHours]:[insights.hours]
        case INSIGHTS_FIELD.CAPACITY:
            return [insights.modelledCapacity]
        case INSIGHTS_FIELD.WORKLOAD:
            return [insights.hours,insights.modelledCapacity]
        case INSIGHTS_FIELD.VALUE:
            return [insights.value]
        case INSIGHTS_FIELD.VALUE_CONTRIBUTION:
            return [Math.round(insights.contributedValue||0)]
        case INSIGHTS_FIELD.REVENUE_CONTRIBUTION:
            return [Math.round(insights.contributedRevenue||0)]
        case INSIGHTS_FIELD.REVENUE_AT_RISK:
            return [Math.round(insights.atRiskRevenue||0)]
        case INSIGHTS_FIELD.REVENUE_MARGIN:
            // the revenue its work creates, less what that work costs
            return [Math.round((insights.contributedRevenue||0) - (insights.allocatedCostsByType?.total||0))]
        case INSIGHTS_FIELD.DERIVED_VALUE:
            return [insights.derivedValue||0]
        case INSIGHTS_FIELD.EXCEPTION_WORK:
            // the cost of an action that is exception work; other nodes have none
            return [insights.exceptionWork ? insights.allocatedCostsByType?.total || 0 : 0]
        case INSIGHTS_FIELD.REVENUE:
            return [insights.revenue]
        case INSIGHTS_FIELD.CARBON:
            return useTotals?[insights.totalCarbon]:[getIndexValue(insights.allocatedCarbonByScope, CARBON_SCOPE.ScopeOne), getIndexValue(insights.allocatedCarbonByScope, CARBON_SCOPE.ScopeTwo), getIndexValue(insights.allocatedCarbonByScope, CARBON_SCOPE.ScopeThree)]
        case INSIGHTS_FIELD.CARBON_COST:
            if (useTotals) {
                if (insights.totalAllocatedCostsByType.total === 0) return undefined
                return [Math.round(100*insights.totalCarbon/insights.totalAllocatedCostsByType.total)/100]
            }else {
                if (insights.allocatedCostsByType.total === 0) return undefined
                return [Math.round(100*insights.allocatedCarbonByScope.total/insights.allocatedCostsByType.total)/100]
            }
        case INSIGHTS_FIELD.CARBON_ITEM:
            if (!insights.numberProcessed) return undefined
            return useTotals?[Math.round(100*insights.totalCarbon/insights.numberProcessed)/100]:[Math.round(100*insights.allocatedCarbonByScope.total/(insights.numberProcessed))/100]
        case INSIGHTS_FIELD.PROCESSED:
            return [insights.numberProcessed]
        case INSIGHTS_FIELD.OUTPUTS:
            return [insights.outputsSupported]
        case INSIGHTS_FIELD.ACTIONS_SUPPORTED:
            return [insights.numActionsSupported]    
        case INSIGHTS_FIELD.INFORMATION_SUPPLIED:
            return [insights.numInfoSupplied]
        case INSIGHTS_FIELD.NONE:
        case INSIGHTS_FIELD.AUTOMATED_ACTIONS:
            return undefined

    }
}

export const getFunctionForFieldType = (type:INSIGHTS_FIELD) => {
        switch (type) {
            case INSIGHTS_FIELD.ANNUAL_HOURS:
            case INSIGHTS_FIELD.HOURS_ITEM:
                return FUNCTIONS.TIME
            case INSIGHTS_FIELD.CAPACITY:
            case INSIGHTS_FIELD.WORKLOAD:
                return FUNCTIONS.CAPACITY
            case INSIGHTS_FIELD.CARBON:
            case INSIGHTS_FIELD.CARBON_COST:
            case INSIGHTS_FIELD.CARBON_ITEM:
                return FUNCTIONS.CARBON
            case INSIGHTS_FIELD.COST:
            case INSIGHTS_FIELD.COST_ITEM:
            case INSIGHTS_FIELD.EXCEPTION_WORK:
                return FUNCTIONS.COSTS
            case INSIGHTS_FIELD.PROCESSED:
                return FUNCTIONS.LOAD
            case INSIGHTS_FIELD.VALUE:
            case INSIGHTS_FIELD.DERIVED_VALUE:
            case INSIGHTS_FIELD.REVENUE:
            case INSIGHTS_FIELD.VALUE_CONTRIBUTION:
            case INSIGHTS_FIELD.REVENUE_CONTRIBUTION:
            case INSIGHTS_FIELD.REVENUE_MARGIN:
            case INSIGHTS_FIELD.REVENUE_AT_RISK:
                return FUNCTIONS.VALUE
            default:
                return undefined
        }
    }

export class InsightsFieldType {
    public type:INSIGHTS_FIELD|undefined
    public shaded:boolean

    constructor(type:INSIGHTS_FIELD, public modelKey:string, public workspaceKey:string=CURRENT_STATE_PROJECT_KEY, public comparisonWorkspaceKey?:string) {
        this.type = type
    }

    get function() {
        return getFunctionForFieldType(this.type)
    }

    isValid() {
        return this.type !== undefined && this.type !== INSIGHTS_FIELD.NONE
    }

    isStacked() {
        return (this.type === INSIGHTS_FIELD.COST || this.type === INSIGHTS_FIELD.CARBON) 
    }

    isCurrency() {
        return (this.type === INSIGHTS_FIELD.COST || this.type === INSIGHTS_FIELD.COST_ITEM || this.type === INSIGHTS_FIELD.REVENUE || this.type === INSIGHTS_FIELD.EXCEPTION_WORK || this.type === INSIGHTS_FIELD.REVENUE_CONTRIBUTION || this.type === INSIGHTS_FIELD.REVENUE_MARGIN || this.type === INSIGHTS_FIELD.REVENUE_AT_RISK)
    }

    getTitle(dataType:NODE_TYPE_DATA_TYPE, superType:NODE_SUPERTYPE) {
        if ((this.type === INSIGHTS_FIELD.CARBON || this.type === INSIGHTS_FIELD.COST || this.type === INSIGHTS_FIELD.ANNUAL_HOURS) && dataTypeUsesTotals(dataType, superType)) return "Total " + this.type.toString()
        return this.type.toString()
    }

    getStacks(useTotals:boolean) {
        if (this.type === INSIGHTS_FIELD.COST) {
            return ["Infrastructure", "System", "People", "Role"]
        }else if (this.type === INSIGHTS_FIELD.CARBON) {
            return useTotals?["Carbon"]:["Scope 1", "Scope 2", "Scope 3"]
        }else if (this.type === INSIGHTS_FIELD.WORKLOAD) {
            return ["Hours", "Capacity"]
        }else return [this.type.toString()]
    }

    getInsights(node:LinqsetNode) {
        return getInsightsForNode(node, this.modelKey, this.workspaceKey)
    }

    getComparison(node:LinqsetNode) {
        return getInsightsForNode(node, this.modelKey, this.comparisonWorkspaceKey)
    }

    getSingleValue(node:LinqsetNode, dataType:NODE_TYPE_DATA_TYPE) {
        const insights = this.getInsights(node)

        return insights && this.getSingleValueFromInsights(insights, dataType)
    }

    getValues(node:LinqsetNode, dataType:NODE_TYPE_DATA_TYPE) {
        const insights = this.getInsights(node)
        return insights && this.getValueFromInsights(insights, dataType)
    }

    getComparisonValues(node:LinqsetNode, dataType:NODE_TYPE_DATA_TYPE) {
        const insights = this.getComparison(node)
        return insights && this.getValueFromInsights(insights, dataType)
    }

    getSingleComparisonValue(node:LinqsetNode, dataType:NODE_TYPE_DATA_TYPE) {
        const insights = this.getComparison(node)
        return insights && this.getSingleValueFromInsights(insights, dataType)
    }

    getUnits() {
        switch (this.type) {
            case INSIGHTS_FIELD.COST:
            case INSIGHTS_FIELD.REVENUE:
                return undefined
            case INSIGHTS_FIELD.CARBON:
                return "kgCO2e"
            case INSIGHTS_FIELD.ACTIONS_SUPPORTED:
            case INSIGHTS_FIELD.AUTOMATED_ACTIONS:
                return "actions"
            case INSIGHTS_FIELD.INFORMATION_SUPPLIED:
                return "info nodes"
            case INSIGHTS_FIELD.HOURS_ITEM:
                return "hours per item"
            case INSIGHTS_FIELD.OUTPUTS:
                return "outputs supported"
            case INSIGHTS_FIELD.ANNUAL_HOURS:
            case INSIGHTS_FIELD.CAPACITY:
            case INSIGHTS_FIELD.WORKLOAD:
                return "hours"
            case INSIGHTS_FIELD.CARBON_COST:
                return "kgCO2e per $"
            case INSIGHTS_FIELD.CARBON_ITEM:
                return "kgCO2e per item"
            case INSIGHTS_FIELD.PROCESSED:
                return "items"
            case INSIGHTS_FIELD.VALUE:
            case INSIGHTS_FIELD.VALUE_CONTRIBUTION:
            case INSIGHTS_FIELD.REVENUE_CONTRIBUTION:
            case INSIGHTS_FIELD.REVENUE_MARGIN:
            case INSIGHTS_FIELD.REVENUE_AT_RISK:
                return undefined
            case INSIGHTS_FIELD.DERIVED_VALUE:
                return "of 100 per item"
            case INSIGHTS_FIELD.COST_ITEM:
                return "per item"
            default:
                return undefined
        }
    }

    getDescription() {
        switch (this.type) {
            case INSIGHTS_FIELD.COST:
                return "Cost allocated to work done."
            case INSIGHTS_FIELD.COST_ITEM:
                return "Cost per item processed"
            case INSIGHTS_FIELD.ACTIONS_SUPPORTED:
                return "Number of actions supported"
            case INSIGHTS_FIELD.HOURS_ITEM:
                return "Average time spent on each item"
            case INSIGHTS_FIELD.ANNUAL_HOURS:
                return "Total hours worked"
            case INSIGHTS_FIELD.CARBON:
                return "Carbon emitted from work"
            case INSIGHTS_FIELD.CARBON_COST:
                return "KG Carbon emitted per dollar of cost"
            case INSIGHTS_FIELD.CARBON_ITEM:
                return "KG Carbon emitted per item processed"
            case INSIGHTS_FIELD.INFORMATION_SUPPLIED:
                return "Number of time system supplies information"
            case INSIGHTS_FIELD.OUTPUTS:
                return "Number of outputs supported"
            case INSIGHTS_FIELD.REVENUE:
                return "Total revenue produced"
            case INSIGHTS_FIELD.CAPACITY:
                return "Total capacity of roles/people"
            case INSIGHTS_FIELD.WORKLOAD:
                return "Remaining capacity of roles/people"
            case INSIGHTS_FIELD.PROCESSED:
                return "Number of info items processed"
            case INSIGHTS_FIELD.VALUE:
                return "Value supported: of the outputs and outcomes that depend on it"
            case INSIGHTS_FIELD.VALUE_CONTRIBUTION:
                return "Its share of the value it helps create, so steps, roles and systems add up to the whole"
            case INSIGHTS_FIELD.REVENUE_CONTRIBUTION:
                return "Its share of the revenue it helps create, so steps, roles and systems add up to the whole"
            case INSIGHTS_FIELD.REVENUE_AT_RISK:
                return "The revenue lost if it stopped: the items only it handles have no other route to the outputs and outcomes"
            case INSIGHTS_FIELD.REVENUE_MARGIN:
                return "Its revenue contribution less the cost of its work: below zero, the work costs more than the revenue it creates"
            case INSIGHTS_FIELD.DERIVED_VALUE:
                return "Value per item derived from how the information is used, 0 to 100 across the organisation"
            case INSIGHTS_FIELD.EXCEPTION_WORK:
                return "Cost of exception work: actions on a detour from the main flow, or marked as such"
            case INSIGHTS_FIELD.AUTOMATED_ACTIONS:
                return "Number of actions that are automated"
            default:
                return undefined    
        }
    }

    getNodeType() {
        switch (this.type) {
            case INSIGHTS_FIELD.COST:
            case INSIGHTS_FIELD.COST_ITEM:
            case INSIGHTS_FIELD.ACTIONS_SUPPORTED:
            case INSIGHTS_FIELD.HOURS_ITEM:
            case INSIGHTS_FIELD.ANNUAL_HOURS:
            case INSIGHTS_FIELD.AUTOMATED_ACTIONS:
            case INSIGHTS_FIELD.EXCEPTION_WORK:
                return NODE_TYPE.ACTION
            case INSIGHTS_FIELD.CARBON:
            case INSIGHTS_FIELD.CARBON_COST:
            case INSIGHTS_FIELD.CARBON_ITEM:
                return NODE_TYPE.INFRASTRUCTURE
            case INSIGHTS_FIELD.INFORMATION_SUPPLIED:
                return NODE_TYPE.SYSTEM
            case INSIGHTS_FIELD.CAPACITY:
            case INSIGHTS_FIELD.WORKLOAD:
                return NODE_TYPE.ROLE
            case INSIGHTS_FIELD.VALUE_CONTRIBUTION:
            case INSIGHTS_FIELD.REVENUE_CONTRIBUTION:
            case INSIGHTS_FIELD.REVENUE_MARGIN:
            case INSIGHTS_FIELD.REVENUE_AT_RISK:
                return NODE_TYPE.ACTION
            case INSIGHTS_FIELD.PROCESSED:
            case INSIGHTS_FIELD.VALUE:
            case INSIGHTS_FIELD.DERIVED_VALUE:
            case INSIGHTS_FIELD.OUTPUTS:
            case INSIGHTS_FIELD.REVENUE:
                return NODE_TYPE.INFORMATION
            default:
                return undefined    
        }
    }

    getColours() {
        switch (this.type) {
            case INSIGHTS_FIELD.COST:
                return [MODEL_COLOURS.INFRASTRUCTURE_COLOR, MODEL_COLOURS.SYSTEM_COLOR, MODEL_COLOURS.PEOPLE_COLOR, MODEL_COLOURS.ROLE_COLOR]
            case INSIGHTS_FIELD.CARBON:
                return [MODEL_COLOURS.CARBON_SCOPE1_COLOUR, MODEL_COLOURS.CARBON_SCOPE2_COLOUR, MODEL_COLOURS.CARBON_SCOPE3_COLOUR]
            case INSIGHTS_FIELD.ACTIONS_SUPPORTED:
            case INSIGHTS_FIELD.ANNUAL_HOURS:
            case INSIGHTS_FIELD.HOURS_ITEM:
            case INSIGHTS_FIELD.AUTOMATED_ACTIONS:
            case INSIGHTS_FIELD.EXCEPTION_WORK:
            case INSIGHTS_FIELD.VALUE_CONTRIBUTION:
            case INSIGHTS_FIELD.REVENUE_CONTRIBUTION:
            case INSIGHTS_FIELD.REVENUE_MARGIN:
            case INSIGHTS_FIELD.REVENUE_AT_RISK:
                return [MODEL_COLOURS.ACTION_COLOR]
            case INSIGHTS_FIELD.CAPACITY:
                return [MODEL_COLOURS.ROLE_COLOR]
            case INSIGHTS_FIELD.WORKLOAD:
                return [MODEL_COLOURS.ACTION_COLOR, MODEL_COLOURS.ROLE_COLOR]
            case INSIGHTS_FIELD.CARBON_COST:
            case INSIGHTS_FIELD.CARBON_ITEM:
                return [MODEL_COLOURS.CARBON_SCOPE1_COLOUR]
            case INSIGHTS_FIELD.PROCESSED:
            case INSIGHTS_FIELD.VALUE:
            case INSIGHTS_FIELD.DERIVED_VALUE:
            case INSIGHTS_FIELD.REVENUE:
            case INSIGHTS_FIELD.INFORMATION_SUPPLIED:
            case INSIGHTS_FIELD.OUTPUTS:
                return [MODEL_COLOURS.INFO_COLOR]
            case INSIGHTS_FIELD.COST_ITEM:
                return [MODEL_COLOURS.DARK_GREEN]
            default:
                return undefined    
        }
    }

    getHoverColours() {
        switch (this.type) {
            case INSIGHTS_FIELD.COST:
                return [MODEL_COLOURS.INFRASTRUCTURE_HOVER_COLOR, MODEL_COLOURS.SYSTEM_HOVER_COLOR, MODEL_COLOURS.PEOPLE_HOVER_COLOR, MODEL_COLOURS.ROLE_HOVER_COLOR]
            case INSIGHTS_FIELD.CARBON:
                return [MODEL_COLOURS.CARBON_SCOPE1_HOVER_COLOUR, MODEL_COLOURS.CARBON_SCOPE2_HOVER_COLOUR, MODEL_COLOURS.CARBON_SCOPE3_HOVER_COLOUR]
            case INSIGHTS_FIELD.ACTIONS_SUPPORTED:
            case INSIGHTS_FIELD.ANNUAL_HOURS:
            case INSIGHTS_FIELD.HOURS_ITEM:
            case INSIGHTS_FIELD.AUTOMATED_ACTIONS:
            case INSIGHTS_FIELD.EXCEPTION_WORK:
                return [MODEL_COLOURS.ACTION_HOVER_COLOR]
            case INSIGHTS_FIELD.CAPACITY:
                return [MODEL_COLOURS.ROLE_HOVER_COLOR]
            case INSIGHTS_FIELD.WORKLOAD:
                return [MODEL_COLOURS.ACTION_HOVER_COLOR, MODEL_COLOURS.ROLE_HOVER_COLOR]
            case INSIGHTS_FIELD.CARBON_COST:
            case INSIGHTS_FIELD.CARBON_ITEM:
                return [MODEL_COLOURS.CARBON_SCOPE1_HOVER_COLOUR]
            case INSIGHTS_FIELD.PROCESSED:
            case INSIGHTS_FIELD.VALUE:
            case INSIGHTS_FIELD.DERIVED_VALUE:
            case INSIGHTS_FIELD.REVENUE:
            case INSIGHTS_FIELD.INFORMATION_SUPPLIED:
            case INSIGHTS_FIELD.OUTPUTS:
                return [MODEL_COLOURS.INFO_HOVER_COLOR]
            case INSIGHTS_FIELD.COST_ITEM:
                return [MODEL_COLOURS.DARK_GREEN]
            default:
                return undefined            
        }
    }

    outcomeApplies() {
        return (this.type === INSIGHTS_FIELD.COST || this.type === INSIGHTS_FIELD.COST_ITEM || this.type === INSIGHTS_FIELD.ANNUAL_HOURS || this.type === INSIGHTS_FIELD.CAPACITY)
    }

    getSingleValueFromInsights(insights:any, dataType:NODE_TYPE_DATA_TYPE) {
        const v = this.getValueFromInsights(insights, dataType)
        if (!v) return 0
        if (this.type === INSIGHTS_FIELD.WORKLOAD && v.length > 1) return v[1]-v[0]
        else return v.length===1?v[0]:v.reduce((t,n) => t +=(n||0), 0)
    }

    public doesFieldTypeApplyForTypeData = (typeData:NodeTypeData) => {
        return this.doesFieldTypeApply(typeData.superType, typeData.dataType)
    }

    public doesFieldTypeApply = (superType:NODE_SUPERTYPE, dataType:NODE_TYPE_DATA_TYPE) => {
        switch (this.type) {
            case INSIGHTS_FIELD.NONE:
            case INSIGHTS_FIELD.COST:
            case INSIGHTS_FIELD.ANNUAL_HOURS:        
            case INSIGHTS_FIELD.HOURS_ITEM:
            case INSIGHTS_FIELD.COST_ITEM:        
            case INSIGHTS_FIELD.REVENUE:
                return true
            case INSIGHTS_FIELD.PROCESSED:
                return dataType !== NODE_TYPE_DATA_TYPE.GROUP && !isEditableOrganisationNode(superType)
            case INSIGHTS_FIELD.VALUE:
                return !isEditableOrganisationNode(superType)
            case INSIGHTS_FIELD.VALUE_CONTRIBUTION:
            case INSIGHTS_FIELD.REVENUE_CONTRIBUTION:
            case INSIGHTS_FIELD.REVENUE_MARGIN:
                // the work and those doing it, and their totals for a model or a group
                return superType === NODE_SUPERTYPE.ACTION || superType === NODE_SUPERTYPE.ROLE || superType === NODE_SUPERTYPE.SYSTEM || dataType === NODE_TYPE_DATA_TYPE.MODELS || dataType === NODE_TYPE_DATA_TYPE.GROUP
            case INSIGHTS_FIELD.REVENUE_AT_RISK:
                // each step, role and system's own: steps can share the same revenue, so it has no total
                return dataType === NODE_TYPE_DATA_TYPE.STANDARD && (superType === NODE_SUPERTYPE.ACTION || superType === NODE_SUPERTYPE.ROLE || superType === NODE_SUPERTYPE.SYSTEM)
            case INSIGHTS_FIELD.DERIVED_VALUE:
                // a ranking of information, so it has no total for a model or a group
                return superType === NODE_SUPERTYPE.INFORMATION && dataType !== NODE_TYPE_DATA_TYPE.GROUP && dataType !== NODE_TYPE_DATA_TYPE.MODELS
            case INSIGHTS_FIELD.EXCEPTION_WORK:
                return superType === NODE_SUPERTYPE.ACTION || dataType === NODE_TYPE_DATA_TYPE.MODELS || dataType === NODE_TYPE_DATA_TYPE.GROUP
            case INSIGHTS_FIELD.CAPACITY:
            case INSIGHTS_FIELD.WORKLOAD:
                return dataType !== NODE_TYPE_DATA_TYPE.GROUP && (dataType === NODE_TYPE_DATA_TYPE.MODELS || isRolePeople(superType)|| superType === NODE_SUPERTYPE.ACTION)
            case INSIGHTS_FIELD.CARBON:
            case INSIGHTS_FIELD.CARBON_COST:
                return (dataType === NODE_TYPE_DATA_TYPE.MODELS || isSystemInfrastructure(superType) || superType === NODE_SUPERTYPE.ACTION || superType === NODE_SUPERTYPE.OUTCOMES || dataType === NODE_TYPE_DATA_TYPE.OUTPUT)
            case INSIGHTS_FIELD.CARBON_ITEM:        
                return dataType !== NODE_TYPE_DATA_TYPE.GROUP && (dataType === NODE_TYPE_DATA_TYPE.MODELS || isSystemInfrastructure(superType) || superType === NODE_SUPERTYPE.ACTION || superType === NODE_SUPERTYPE.OUTCOMES || dataType === NODE_TYPE_DATA_TYPE.OUTPUT)
            case INSIGHTS_FIELD.OUTPUTS:
                return (superType === NODE_SUPERTYPE.OUTCOMES || dataType === NODE_TYPE_DATA_TYPE.OUTPUT) && dataType !== NODE_TYPE_DATA_TYPE.GROUP
            case INSIGHTS_FIELD.ACTIONS_SUPPORTED:
                return dataType === NODE_TYPE_DATA_TYPE.GROUP || dataType === NODE_TYPE_DATA_TYPE.MODELS || isRoleSystem(superType)
            case INSIGHTS_FIELD.INFORMATION_SUPPLIED:
                return dataType === NODE_TYPE_DATA_TYPE.GROUP || dataType === NODE_TYPE_DATA_TYPE.MODELS || superType === NODE_SUPERTYPE.SYSTEM
            case INSIGHTS_FIELD.AUTOMATED_ACTIONS:
                return dataType === NODE_TYPE_DATA_TYPE.GROUP || dataType === NODE_TYPE_DATA_TYPE.MODELS
        }
    }

    getValueFromInsights(insights:any, dataType:NODE_TYPE_DATA_TYPE):number[] {
        if (!this.type || !insights) return undefined

        if (insights.superTypes) {
            // is model insights
            switch (this.type) {
                case INSIGHTS_FIELD.COST:
                    return [insights.superTypes[NODE_SUPERTYPE.INFRASTRUCTURE]?.allocatedCost||0, insights.superTypes[NODE_SUPERTYPE.SYSTEM]?.allocatedCost||0, insights.superTypes[NODE_SUPERTYPE.PEOPLE]?.allocatedCost||0, insights.superTypes[NODE_SUPERTYPE.ROLE]?.allocatedCost||0]
                case INSIGHTS_FIELD.ANNUAL_HOURS:
                    return [insights.superTypes[NODE_SUPERTYPE.ACTION].hours]
                case INSIGHTS_FIELD.VALUE:
                case INSIGHTS_FIELD.VALUE_CONTRIBUTION:
                    // a model's contributions add up to the value it creates
                    return [insights.outputValue]
                case INSIGHTS_FIELD.REVENUE_CONTRIBUTION:
                    return [insights.outputRevenue]
                case INSIGHTS_FIELD.REVENUE_MARGIN:
                    return [Math.round((insights.outputRevenue||0) - getTotalModelCost(insights))]
                case INSIGHTS_FIELD.EXCEPTION_WORK:
                    return [insights.exceptionCost||0]
                case INSIGHTS_FIELD.REVENUE:
                    return [insights.outputRevenue]
                case INSIGHTS_FIELD.CARBON:
                    return [getIndexValue(insights.allocatedCarbonByScope, CARBON_SCOPE.ScopeOne), getIndexValue(insights.allocatedCarbonByScope, CARBON_SCOPE.ScopeTwo), getIndexValue(insights.allocatedCarbonByScope, CARBON_SCOPE.ScopeThree)]
                case INSIGHTS_FIELD.CARBON_COST:
                    if (getTotalModelCost(insights) === 0) return undefined
                    return [Math.round(100*insights.allocatedCarbonByScope.total/getTotalModelCost(insights))/100]
                case INSIGHTS_FIELD.ACTIONS_SUPPORTED:
                    return [insights.superTypes[NODE_SUPERTYPE.ACTION].number]
                case INSIGHTS_FIELD.PROCESSED:
                    return dataType===NODE_TYPE_DATA_TYPE.INPUT?[insights.numInputsProcessed]:[insights.numOutputsProcessed]
                case INSIGHTS_FIELD.HOURS_ITEM:
                    if (insights.numOutputsProcessed===0) return undefined
                    return [Math.round(100*insights.superTypes[NODE_SUPERTYPE.ACTION].hours/insights.numOutputsProcessed)/100]
                case INSIGHTS_FIELD.COST_ITEM:
                    if (insights.numOutputsProcessed===0) return undefined
                    return [Math.round(100*(getTotalModelCost(insights))/insights.numOutputsProcessed)/100]
                case INSIGHTS_FIELD.CAPACITY:
                    return [insights.modelledCapacity]
                case INSIGHTS_FIELD.WORKLOAD:
                    return [insights.superTypes[NODE_SUPERTYPE.ROLE].hours, insights.modelledCapacity]
                case INSIGHTS_FIELD.CARBON_ITEM:
                    return [Math.round(100*insights.allocatedCarbonByScope.total/(insights.numOutputsProcessed||1))/100]
                case INSIGHTS_FIELD.OUTPUTS:
                    return [insights.numOutputs]
                case INSIGHTS_FIELD.INFORMATION_SUPPLIED:
                    return [insights.superTypes[NODE_SUPERTYPE.INFORMATION].number]
                case INSIGHTS_FIELD.AUTOMATED_ACTIONS:
                    return [insights.actionTypes[ACTION_TYPE.AUTOMATED].number]

            }
        }else {
            return getInsightsValueForNode(this.type, insights, dataType)
        }
        return null
    }
}
